import './Invoices.scss';
import * as Dfns from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ASSET_TRAFFICKING,
  ELECTRONIC_DETECTION,
  InvoiceRow,
  ManualInvoiceOptions,
  SERVER_EXPENSE,
  UpdateManualInvoiceRowsBody
} from '@blisspointmedia/bpm-types/dist/BillingAutomation';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import {
  Appearance,
  Design,
  Intent,
  Placement,
  Size,
  StretchMode
} from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { EmptyState } from '@Components/EmptyState';
import { InteractiveSpan } from '@Components/InteractiveEl';
import { ModalEditWidget } from '@Components/ModalEditWidget';
import {
  ModalEditWidgetHandle,
  SaveChangesInput,
  TableRow
} from '@Components/ModalEditWidget/modal-edit-constants';
import { DownloadDropdown } from '@src/Components/DownloadDropdown/DownloadDropdown';
import { useSetAreYouSure, useSetError } from '../../redux/modals';
import { exportToExcel } from '../../utils/download-utils';
import {
  awaitJSON,
  BillingAutomationLambdaFetch,
  DagLambdaFetch,
  fetchCometSelfInvoke
} from '../../utils/fetch-utils';
import { LocationType } from '../../utils/routing/location-constants';
import { useBpmNavigate } from '../../utils/routing/useBpmNavigate';
import { SegmentRow } from '../SegmentationMapping/segmentation-mapping-constants';
import { isBillingFullyCoveredForDateRange } from '../SegmentationMapping/segmentationMappingUtils';
import { AddInvoiceRowModal } from './AddInvoiceRowModal';
import { BILLING_CORRECTION_JIRA_URL } from './billing-constants';
import {
  BILLING_JOB_DAG,
  BILLING_JOB_TASK,
  BILLING_JOB_TASK_NAME,
  fetchBillingSegmentCoverage,
  getInvoiceHeaders,
  invoiceMonthKey,
  invoiceMonthLabel,
  invoiceRowsToExportData,
  isEditableManualInvoice,
  MONTH_KEY_FORMAT
} from './billing-utils';
import { NotificationBanner } from './NotificationBanner';

interface InvoicesProps {
  company: string;
  userEmail: string;
}

interface SignResult {
  calculationIds: string[];
  signedOffBy: string | null;
  signedOffAt: string | null;
}

const HIDDEN_EXPENSE_CHANNELS = [SERVER_EXPENSE, ELECTRONIC_DETECTION, ASSET_TRAFFICKING];

export const Invoices = ({ company, userEmail }: InvoicesProps) => {
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();
  const navigate = useBpmNavigate();
  const goToSegmentationLabeling = useCallback(
    () =>
      navigate({
        type: LocationType.Client,
        path: 'segmentation-mapping/labeling',
        prettyName: 'Segmentation Mapping'
      }),
    [navigate]
  );
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [coverageRows, setCoverageRows] = useState<SegmentRow[]>([]);
  const [isFetching, setIsFetching] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const inFlightRef = useRef<Set<string>>(new Set());
  const [showRefreshConfirm, setShowRefreshConfirm] = useState(false);
  const [showRefreshSuccess, setShowRefreshSuccess] = useState(false);

  const widgetRef = useRef<ModalEditWidgetHandle<InvoiceRow>>(null);
  const [manualInvoiceOptions, setManualInvoiceOptions] = useState<
    ManualInvoiceOptions | undefined
  >();
  const [showModal, setShowModal] = useState(false);
  const [editingRow, setEditingRow] = useState<InvoiceRow | undefined>();
  const [editingRowKey, setEditingRowKey] = useState<string | undefined>();
  // Freehand out-of-app platforms typed into the add-row modal, persisted here (rather than in
  // the modal itself) so they're offered again across multiple add-row sessions.
  const [addedPlatformOptions, setAddedPlatformOptions] = useState<string[]>([]);

  const loadData = useCallback(async () => {
    setIsFetching(true);
    try {
      const res = await BillingAutomationLambdaFetch('/fetchBillingInvoices', {
        params: { cid: company }
      });
      const { invoices: fetched } = await awaitJSON<{ invoices: InvoiceRow[] }>(res);
      setInvoices(fetched);
      // Default to the most recent month present (rows arrive sorted newest-first).
      setSelectedMonth(fetched.length ? invoiceMonthKey(fetched[0]) : '');
    } catch (e) {
      setError({ message: e.message, title: 'Failed to load invoices', reportError: e });
    } finally {
      setIsFetching(false);
    }
  }, [company, setError]);

  const loadCoverage = useCallback(async () => {
    try {
      const rows = await fetchBillingSegmentCoverage(company);
      setCoverageRows(rows);
    } catch (e) {
      setError({
        message: e.message,
        title: 'Failed to load billing-segment coverage',
        reportError: e
      });
    }
  }, [company, setError]);

  const loadOptions = useCallback(async () => {
    try {
      const options = await fetchCometSelfInvoke<{ cid: string }, ManualInvoiceOptions>(
        BillingAutomationLambdaFetch,
        '/fetchManualInvoiceOptions',
        { cid: company },
        { method: 'GET', noTimeout: true }
      );
      setManualInvoiceOptions(options);
    } catch (e) {
      setError({ message: e.message, title: 'Failed to load add-row options', reportError: e });
    }
  }, [company, setError]);

  useEffect(() => {
    // `company` defaults to '' in Redux before it hydrates; wait for a real value.
    if (!company) {
      return;
    }
    loadData();
    loadCoverage();
    loadOptions();
  }, [company, loadData, loadCoverage, loadOptions]);

  const monthOptions = useMemo(() => {
    const keys = [...new Set(invoices.map(invoiceMonthKey))].sort().reverse();
    return keys.map(key => ({ label: invoiceMonthLabel(key), value: key }));
  }, [invoices]);

  const mostRecentBillingMonth = monthOptions[0]?.value ?? '';

  const visibleInvoices = useMemo(
    () =>
      invoices.filter(
        row =>
          invoiceMonthKey(row) === selectedMonth && !HIDDEN_EXPENSE_CHANNELS.includes(row.channel)
      ),
    [invoices, selectedMonth]
  );

  const hasCoverageGap = useMemo(() => {
    if (!selectedMonth) {
      return false;
    }
    const monthDate = Dfns.parse(selectedMonth, MONTH_KEY_FORMAT, new Date());
    const coverageRange = {
      start: Dfns.format(Dfns.startOfMonth(monthDate), 'yyyy-MM-dd'),
      end: Dfns.format(Dfns.endOfMonth(monthDate), 'yyyy-MM-dd')
    };
    return coverageRows.some(row => !isBillingFullyCoveredForDateRange(row, coverageRange));
  }, [coverageRows, selectedMonth]);

  const handleToggleSignOff = useCallback(
    async (row: InvoiceRow) => {
      if (inFlightRef.current.has(row.calculationId)) return;
      inFlightRef.current.add(row.calculationId);

      const signing = row.signedOffAt === null;
      const previous = { signedOffBy: row.signedOffBy, signedOffAt: row.signedOffAt };
      const optimistic = signing
        ? { signedOffBy: userEmail, signedOffAt: new Date().toISOString() }
        : { signedOffBy: null, signedOffAt: null };

      setInvoices(prev =>
        prev.map(invoice =>
          invoice.calculationId === row.calculationId ? { ...invoice, ...optimistic } : invoice
        )
      );

      try {
        const res = await BillingAutomationLambdaFetch('/signInvoice', {
          method: 'PATCH',
          body: {
            calculationIds: [row.calculationId],
            sign: signing,
            signator: signing ? userEmail : undefined
          }
        });
        const result = await awaitJSON<SignResult>(res);
        const updatedIds = new Set(result.calculationIds);
        setInvoices(prev =>
          prev.map(invoice =>
            updatedIds.has(invoice.calculationId)
              ? { ...invoice, signedOffBy: result.signedOffBy, signedOffAt: result.signedOffAt }
              : invoice
          )
        );
      } catch (e) {
        setInvoices(prev =>
          prev.map(invoice =>
            invoice.calculationId === row.calculationId ? { ...invoice, ...previous } : invoice
          )
        );
        setError({ message: e.message, title: 'Failed to sign-off', reportError: e });
      } finally {
        inFlightRef.current.delete(row.calculationId);
      }
    },
    [userEmail, setError]
  );

  const stageRow = useCallback(
    (row: InvoiceRow) => {
      if (editingRowKey !== undefined) {
        widgetRef.current?.trackModify(editingRowKey, row);
      } else {
        widgetRef.current?.trackAdd(row);
        // New rows always belong to the most recent billing month -- jump the view there so the
        // row appears where it actually lives instead of under whatever month tab was open.
        setSelectedMonth(mostRecentBillingMonth);
      }
    },
    [editingRowKey, mostRecentBillingMonth]
  );

  const handleEditRow = useCallback((row: TableRow<InvoiceRow>) => {
    setEditingRow(row);
    setEditingRowKey(row.rowKey);
    setShowModal(true);
  }, []);

  const headers = useMemo(
    // handleToggleSignOff reads inFlightRef.current, but only inside the async handler —
    // never during render — so this ref-in-memo warning is a false positive.
    () =>
      // eslint-disable-next-line react-hooks/refs
      getInvoiceHeaders({
        onToggleSignOff: handleToggleSignOff,
        onEditRow: handleEditRow,
        onDeleteRow: (row: TableRow<InvoiceRow>) =>
          setAreYouSure({
            title: 'Delete Manual Invoice Row',
            message: 'You are about to delete this manual invoice row, are you sure?',
            intent: Intent.ERROR,
            cancelText: 'Never mind',
            okayText: 'Yes',
            promise: false,
            onOkay: () => widgetRef.current?.trackDelete(row.rowKey)
          }),
        userEmail
      }),
    [handleToggleSignOff, handleEditRow, setAreYouSure, userEmail]
  );

  const handleSaveManual = useCallback(
    async ({ addedData, modifiedData, deletedData, cleanup }: SaveChangesInput<InvoiceRow>) => {
      try {
        const body: UpdateManualInvoiceRowsBody = {
          cid: company,
          billingMonth: mostRecentBillingMonth,
          added: addedData,
          modified: modifiedData,
          deleted: deletedData,
          email: userEmail
        };
        await BillingAutomationLambdaFetch('/updateManualInvoiceRows', { method: 'POST', body });
        cleanup();
        await loadData();
      } catch (e) {
        // 409 = the relationship's invoice for this month was already exported (see
        // assertRelationshipNotExported in bpm-lambdas/billingautomation/manualInvoices.ts) --
        const isExportLocked = e.status === 409;
        setError({
          title: 'Failed to save invoice rows',
          message: isExportLocked ? (
            <>
              {e.message}{' '}
              <a href={BILLING_CORRECTION_JIRA_URL} target='_blank' rel='noreferrer'>
                Submit a billing correction request
              </a>
              .
            </>
          ) : (
            e.message
          ),
          reportError: e
        });
      }
    },
    [company, mostRecentBillingMonth, userEmail, loadData, setError]
  );

  // No month selected means no invoices have ever been generated for this client -- Refresh falls
  // back to the job's default trailing window in that case (see handleRefresh).
  const refreshTargetLabel = selectedMonth
    ? invoiceMonthLabel(selectedMonth)
    : 'the most recent two billing periods';

  const handleDownload = useCallback(() => {
    exportToExcel(invoiceRowsToExportData(visibleInvoices), `${company}_invoices_${selectedMonth}`);
  }, [visibleInvoices, company, selectedMonth]);

  const handleRefresh = useCallback(async () => {
    setShowRefreshConfirm(false);
    try {
      await DagLambdaFetch('/start_single_job', {
        method: 'POST',
        body: {
          dag: BILLING_JOB_DAG,
          task: BILLING_JOB_TASK,
          task_name: BILLING_JOB_TASK_NAME,
          override: true,
          // No month selected means no invoices have ever been generated for this client -- an
          // empty billingMonths tells the job to fall back to its default trailing window instead.
          extra_base64: { billingMonths: selectedMonth ? [selectedMonth] : [], cid: company }
        }
      });
      setShowRefreshSuccess(true);
    } catch (e) {
      setError({ message: e.message, title: 'Failed to start refresh', reportError: e });
    }
  }, [selectedMonth, company, setError]);

  const renderContent = () => (
    <ModalEditWidget<InvoiceRow>
      ref={widgetRef}
      name='Billing Sheets'
      addButtonText='Add a Row'
      className='invoices-widget'
      headers={headers}
      initialTableData={visibleInvoices}
      isLoading={isFetching}
      selectorOptions={{}}
      filterBar
      rowHeight={50}
      headerRightActions={
        <>
          <Button
            size={Size.LG}
            appearance={Appearance.OUTLINED}
            onClick={() => setShowRefreshConfirm(true)}
          >
            Refresh
          </Button>
          <DownloadDropdown
            size={Size.LG}
            menuOptions={['XLSX']}
            design={Design.BRAND}
            appearance={Appearance.OUTLINED}
            disabled={!visibleInvoices.length}
            onClickOptions={[handleDownload]}
          />
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            label='Month'
            value={selectedMonth}
            options={monthOptions}
            onChange={setSelectedMonth}
            stretch={StretchMode.FIT}
          />
        </>
      }
      emptyContent={
        <EmptyState
          title='No invoices yet.'
          subtitle='Invoices appear here once billing has run for this client.'
        />
      }
      customEditor={{
        onRowClick: row => {
          if (isEditableManualInvoice(row)) {
            handleEditRow(row);
          }
        },
        onAddClick: () => {
          setEditingRow(undefined);
          setEditingRowKey(undefined);
          setShowModal(true);
        }
      }}
      onSaveModal={handleSaveManual}
    />
  );

  return (
    <div className='invoices-tab'>
      {hasCoverageGap && (
        <NotificationBanner
          intent={Intent.WARNING}
          message={
            <>
              Warning: some campaigns are missing values for billing segments for{' '}
              {invoiceMonthLabel(selectedMonth)}. Billable, Include in Fee Calc or Billing Category
              columns may be incorrect. Update billing segment values{' '}
              <InteractiveSpan
                className='notification-banner-link'
                onClick={goToSegmentationLabeling}
              >
                here
              </InteractiveSpan>{' '}
              before continuing.
            </>
          }
        />
      )}
      {renderContent()}
      {showModal && (
        <AddInvoiceRowModal
          show={showModal}
          onClose={() => setShowModal(false)}
          onSave={stageRow}
          options={manualInvoiceOptions}
          billingMonth={editingRow ? invoiceMonthKey(editingRow) : mostRecentBillingMonth}
          existingRow={editingRow}
          addedPlatformOptions={addedPlatformOptions}
          setAddedPlatformOptions={setAddedPlatformOptions}
        />
      )}
      <AlertDialog
        show={showRefreshConfirm}
        onHide={() => setShowRefreshConfirm(false)}
        intent={Intent.WARNING}
        title='Refresh current invoice?'
        body={`This re-calculates billing for ${refreshTargetLabel} and overwrites unsigned rows. Signed-off rows are preserved.`}
        primaryAction={{ onClick: handleRefresh, label: 'Refresh' }}
        secondaryAction={{ onClick: () => setShowRefreshConfirm(false) }}
      />
      <AlertDialog
        show={showRefreshSuccess}
        onHide={() => setShowRefreshSuccess(false)}
        intent={Intent.SUCCESS}
        title='Refresh started'
        body={`Billing is regenerating for ${refreshTargetLabel}. Check back in ~30 minutes for an updated invoice.`}
      />
    </div>
  );
};
